import { useMemo } from 'react';
import { SEVERITIES, type Anomaly, type Severity } from '@soc/shared';
import { useAppSelector } from '../../app/hooks';

/** Every alert across all rule groups, flattened (unordered). */
export function useAllAlerts(): Anomaly[] {
  const byRule = useAppSelector((s) => s.alerts.byRule);
  return useMemo(() => Object.values(byRule).flat(), [byRule]);
}

/**
 * Alerts shown in the feed (both acknowledged + not), newest first. A rule's
 * alerts only exist while it's running/paused — Stop purges them — so there's no
 * enabled-rule filter here anymore.
 */
export function useVisibleAlerts(): Anomaly[] {
  const all = useAllAlerts();
  return useMemo(() => [...all].sort((a, b) => b.timestamp.localeCompare(a.timestamp)), [all]);
}

/** Unacknowledged alerts — what shows as pins on the map. */
export function useActiveAlerts(): Anomaly[] {
  const visible = useVisibleAlerts();
  return useMemo(() => visible.filter((a) => !a.acknowledged), [visible]);
}

/** The alert backing the current single-pin popup, if any (undefined once purged). */
export function useSelectedAlert(): Anomaly | undefined {
  const selection = useAppSelector((s) => s.mapUi.selection);
  const all = useAllAlerts();
  return useMemo(() => {
    if (selection?.kind !== 'alert') return undefined;
    return all.find((a) => a.id === selection.alertId);
  }, [selection, all]);
}

export function countBySeverity(alerts: Anomaly[]): Record<Severity, number> {
  return alerts.reduce(
    (acc, a) => {
      acc[a.severity] += 1;
      return acc;
    },
    Object.fromEntries(SEVERITIES.map((s) => [s, 0])) as Record<Severity, number>,
  );
}
